import { zodResolver } from '@hookform/resolvers/zod';
import { InfoIcon, MailIcon, Trash2Icon, UserPlusIcon, UsersIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { AccessLevelTiles } from '@/components/AccessLevelTiles';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CopyButton } from '@/components/CopyButton';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { AccessLevelCards } from '@/components/JobAssignmentPicker';
import { PageIntro } from '@/components/PageIntro';
import { AccessLevelBadge, RoleBadge } from '@/components/StatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useUsers } from '@/features/org/queries';
import { useJobContext } from '@/layouts/JobLayout';
import { errorMessage } from '@/lib/api';
import { useAuth, useMe } from '@/lib/auth';
import { fmtDate, fmtDateTime, fmtRelative } from '@/lib/format';
import { LEVEL_DESCRIPTION, LEVEL_LABEL, jobRoleForLevel, levelForJobRole } from '@/lib/roles';
import type { AccessLevel, JobRole, MemberDto } from '@/lib/types';
import { initials } from '@/lib/utils';
import { useAddMember, useInviteMember, useMembers, useRemoveMember, useRevokeJobInvitation, useUpdateMember } from './queries';

type PmLevel = 'pm_admin' | 'pm_user';

/** PM Admin / PM User choice; PM Admin is only offered to people who may grant sending rights. */
function pmLevelOptions(canGrant: boolean) {
  return [
    { value: 'pm_admin' as PmLevel, label: LEVEL_LABEL.pm_admin, hint: canGrant ? LEVEL_DESCRIPTION.pm_admin : 'Only PM Admins and Super Users can make someone a PM Admin.' },
    { value: 'pm_user' as PmLevel, label: LEVEL_LABEL.pm_user, hint: LEVEL_DESCRIPTION.pm_user },
  ];
}

const levelOf = (m: MemberDto): AccessLevel => (m.tenantRole === 'org_admin' ? 'super_user' : levelForJobRole(m.role));

/**
 * Who works this job and what each person may do. Three access tiles explain the levels and count who holds
 * each; the table beneath lists everyone with their level as a control where you may change it. Invitations
 * that have not been accepted sit in their own card.
 */
export function TeamPage() {
  const { job, permissions } = useJobContext();
  const me = useMe();
  const { isOrgAdmin } = useAuth();
  const members = useMembers(job.id);
  const updateMember = useUpdateMember(job.id);
  const removeMember = useRemoveMember(job.id);
  const revoke = useRevokeJobInvitation(job.id);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [removing, setRemoving] = useState<MemberDto | null>(null);
  const [bulkRemoving, setBulkRemoving] = useState<MemberDto[] | null>(null);
  const [busy, setBusy] = useState(false);

  const canTouch = (role: JobRole) => permissions.manageTeam && (role !== 'admin' || permissions.grantSend);
  const editable = (m: MemberDto) => m.id !== me.user.id && m.tenantRole !== 'org_admin' && canTouch(m.role);

  const changeRole = async (m: MemberDto, role: JobRole) => {
    try {
      await updateMember.mutateAsync({ userId: m.id, role });
      toast.success(`${m.fullName} is now a ${LEVEL_LABEL[levelForJobRole(role)]} on ${job.name}`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  /** Apply one change to every selected member, reporting how many worked. */
  const forEachMember = async (list: MemberDto[], label: string, fn: (m: MemberDto) => Promise<unknown>) => {
    setBusy(true);
    let ok = 0;
    for (const m of list) {
      try {
        await fn(m);
        ok++;
      } catch {
        /* summarised below */
      }
    }
    setBusy(false);
    if (ok === list.length) toast.success(`${label} for ${ok} ${ok === 1 ? 'person' : 'people'}`);
    else toast.warning(`${label} for ${ok} of ${list.length}; the rest failed`);
  };

  const team = members.data?.items ?? [];
  const counts: Record<AccessLevel, number> = { super_user: 0, pm_admin: 0, pm_user: 0 };
  for (const m of team) counts[levelOf(m)]++;

  const levelControl = (m: MemberDto, size: 'sm' | 'default' = 'sm') => {
    if (m.tenantRole === 'org_admin') return <AccessLevelBadge level="super_user" />;
    if (!editable(m)) return <RoleBadge role={m.role} />;
    return (
      <Select value={m.role} onValueChange={(v) => void changeRole(m, v as JobRole)} disabled={updateMember.isPending || busy}>
        <SelectTrigger size={size} className="w-[8.5rem]" aria-label={`Access level for ${m.fullName}`} onClick={(e) => e.stopPropagation()}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="admin" disabled={!permissions.grantSend}>
            {LEVEL_LABEL.pm_admin}
          </SelectItem>
          <SelectItem value="user">{LEVEL_LABEL.pm_user}</SelectItem>
        </SelectContent>
      </Select>
    );
  };

  const columns: Array<Column<MemberDto>> = [
    {
      id: 'name',
      header: 'Name',
      sortValue: (m) => m.fullName,
      searchValue: (m) => `${m.fullName} ${m.email}`,
      csv: (m) => m.fullName,
      cell: (m) => <Person m={m} you={m.id === me.user.id} />,
    },
    { id: 'email', header: 'Email', sortValue: (m) => m.email, className: 'hidden lg:table-cell', cell: (m) => <span className="text-muted-foreground">{m.email}</span> },
    {
      id: 'access',
      header: 'Access on this job',
      sortValue: (m) => (m.tenantRole === 'org_admin' ? 'a' : m.role === 'admin' ? 'b' : 'c'),
      csv: (m) => LEVEL_LABEL[levelOf(m)],
      cell: (m) => levelControl(m),
    },
    { id: 'since', header: 'Member since', sortValue: (m) => m.createdAt, csv: (m) => m.createdAt, className: 'hidden xl:table-cell', cell: (m) => <span className="text-muted-foreground" title={fmtDateTime(m.createdAt)}>{fmtDate(m.createdAt)}</span> },
    {
      id: 'actions',
      header: '',
      noExport: true,
      align: 'right',
      className: 'w-14',
      cell: (m) =>
        editable(m) ? (
          <Button variant="ghost" size="icon-sm" aria-label={`Remove ${m.fullName}`} onClick={() => setRemoving(m)}>
            <Trash2Icon />
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageIntro
        title="Team"
        description={`The people who run ${job.name}. Access is per job: someone can be a PM Admin here and a PM User elsewhere.`}
        actions={
          permissions.manageTeam ? (
            <>
              {isOrgAdmin && (
                <Button variant="outline" onClick={() => setAddOpen(true)}>
                  <UsersIcon /> Add existing user
                </Button>
              )}
              <Button onClick={() => setInviteOpen(true)}>
                <UserPlusIcon /> Invite by email
              </Button>
            </>
          ) : undefined
        }
      />

      {/* ------------------------------------------------------------ access levels */}
      <AccessLevelTiles counts={members.data ? counts : undefined} />

      {permissions.manageTeam && !permissions.grantSend && (
        <Alert>
          <InfoIcon />
          <AlertTitle>You can manage PM Users on this job</AlertTitle>
          <AlertDescription>Making someone a PM Admin, or changing an existing PM Admin, needs a PM Admin or Super User.</AlertDescription>
        </Alert>
      )}

      <DataTable
        data={members.data?.items}
        loading={members.isPending}
        error={members.isError ? members.error : undefined}
        onRetry={() => void members.refetch()}
        columns={columns}
        rowId={(m) => m.id}
        searchPlaceholder="Search the team…"
        defaultSort={{ id: 'name', dir: 'asc' }}
        exportName={`${job.name}-team`}
        filters={[
          {
            key: 'level',
            label: 'Access',
            options: [
              { value: 'super_user', label: LEVEL_LABEL.super_user },
              { value: 'pm_admin', label: LEVEL_LABEL.pm_admin },
              { value: 'pm_user', label: LEVEL_LABEL.pm_user },
            ],
            predicate: (m, v) => levelOf(m) === v,
            counts: true,
          },
        ]}
        emptyState={
          <EmptyState
            icon={UsersIcon}
            title="No team members yet"
            description="Invite the project managers who run this job so they can work it with you."
            action={
              permissions.manageTeam ? (
                <Button onClick={() => setInviteOpen(true)}>
                  <UserPlusIcon /> Invite by email
                </Button>
              ) : undefined
            }
          />
        }
        selectable={permissions.manageTeam}
        selectDisabled={(m) => !editable(m)}
        bulkActions={({ selected, clear }) => (
          <>
            {permissions.grantSend && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => void forEachMember(selected.filter((m) => m.role !== 'admin'), 'PM Admin set', (m) => updateMember.mutateAsync({ userId: m.id, role: 'admin' })).then(clear)}>
                Make PM Admin
              </Button>
            )}
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void forEachMember(selected.filter((m) => m.role !== 'user'), 'PM User set', (m) => updateMember.mutateAsync({ userId: m.id, role: 'user' })).then(clear)}>
              Make PM User
            </Button>
            <Button size="sm" variant="outline" className="text-destructive" disabled={busy} onClick={() => setBulkRemoving(selected)}>
              <Trash2Icon /> Remove from job
            </Button>
          </>
        )}
        mobileCard={(m) => (
          <div className="flex items-start justify-between gap-3">
            <Person m={m} you={m.id === me.user.id} email since={m.createdAt} />
            <div className="flex shrink-0 flex-col items-end gap-2">
              {levelControl(m, 'sm')}
              {editable(m) && (
                <Button variant="ghost" size="xs" className="text-muted-foreground" onClick={() => setRemoving(m)}>
                  <Trash2Icon /> Remove
                </Button>
              )}
            </div>
          </div>
        )}
      />

      {/* ------------------------------------------------------------ invitations */}
      {members.isPending ? (
        <Skeleton className="h-10 w-full" />
      ) : (
        members.data &&
        members.data.pendingInvitations.length > 0 && (
          <section className="bg-card border" aria-label="Pending invitations">
            <div className="flex items-baseline justify-between gap-3 px-5 pt-4 pb-3">
              <h3 className="text-sm font-semibold tracking-tight">Pending invitations</h3>
              <span className="text-muted-foreground text-xs">Waiting to be accepted</span>
            </div>
            <ul className="divide-y border-t">
              {members.data.pendingInvitations.map((i) => {
                const here = i.jobs.find((j) => j.id === job.id);
                const role = here?.role ?? i.jobRole ?? 'user';
                const fromOrg = i.jobId !== job.id;
                const canRevoke = permissions.manageTeam && (!fromOrg || isOrgAdmin) && (role !== 'admin' || permissions.grantSend);
                return (
                  <li key={i.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
                    <Avatar className="size-8">
                      <AvatarFallback>
                        <MailIcon className="size-3.5" aria-hidden />
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{i.email}</div>
                      <div className="text-muted-foreground text-xs">
                        Sent {fmtRelative(i.createdAt)} · expires {fmtDate(i.expiresAt)}
                        {fromOrg && ` · from the Users page${i.jobs.length > 1 ? ` (${i.jobs.length} jobs)` : ''}`}
                      </div>
                    </div>
                    <RoleBadge role={role} />
                    {canRevoke && (
                      <Button variant="ghost" size="sm" disabled={revoke.isPending} onClick={() => void revoke.mutateAsync(i.id).then(() => toast.success('Invitation revoked')).catch((e: unknown) => toast.error(errorMessage(e)))}>
                        Revoke
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )
      )}

      <ConfirmDialog
        open={!!bulkRemoving}
        onOpenChange={(o) => !o && setBulkRemoving(null)}
        title={`Remove ${bulkRemoving?.length ?? 0} ${bulkRemoving?.length === 1 ? 'person' : 'people'} from ${job.name}?`}
        description="They keep their account and any other jobs; they just lose access to this one."
        confirmLabel="Remove"
        destructive
        loading={busy}
        onConfirm={async () => {
          if (!bulkRemoving) return;
          await forEachMember(bulkRemoving, 'Removed', (m) => removeMember.mutateAsync(m.id));
          setBulkRemoving(null);
        }}
      />
      <InviteMemberDialog open={inviteOpen} onOpenChange={setInviteOpen} canGrant={permissions.grantSend} />
      {isOrgAdmin && <AddExistingUserDialog open={addOpen} onOpenChange={setAddOpen} existingIds={team.map((m) => m.id)} />}
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove ${removing?.fullName} from this job?`}
        description="They keep their account but lose access to this job's contacts and history."
        confirmLabel="Remove"
        destructive
        loading={removeMember.isPending}
        onConfirm={async () => {
          if (!removing) return;
          try {
            await removeMember.mutateAsync(removing.id);
            toast.success('Member removed');
            setRemoving(null);
          } catch (err) {
            toast.error(errorMessage(err));
          }
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------- pieces

/** Initials, name and (on request) email and tenure; the table's name cell and the phone card's left side. */
function Person({ m, you, email, since }: { m: MemberDto; you: boolean; email?: boolean; since?: string }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar className="size-8 shrink-0">
        <AvatarFallback className="text-[11px]">{initials(m.fullName)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium">{m.fullName}</span>
          {you && <span className="text-muted-foreground border px-1 text-[10px] leading-4 font-medium">you</span>}
        </div>
        {(email || since) && (
          <div className="text-muted-foreground truncate text-xs">
            {email && m.email}
            {email && since && ' · '}
            {since && `since ${fmtDate(since)}`}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- dialogs

const inviteSchema = z.object({ email: z.email('Enter a valid email address'), level: z.enum(['pm_admin', 'pm_user']), message: z.string().trim().max(500) });
type InviteValues = z.infer<typeof inviteSchema>;

function InviteMemberDialog({ open, onOpenChange, canGrant }: { open: boolean; onOpenChange: (o: boolean) => void; canGrant: boolean }) {
  const { job } = useJobContext();
  const invite = useInviteMember(job.id);
  const [error, setError] = useState<string | null>(null);
  const [acceptUrl, setAcceptUrl] = useState<string | null>(null);
  const [existingUser, setExistingUser] = useState(false);
  const form = useForm<InviteValues>({ resolver: zodResolver(inviteSchema), defaultValues: { email: '', level: 'pm_user', message: '' } });

  useEffect(() => {
    if (!open) {
      form.reset();
      setError(null);
      setAcceptUrl(null);
      setExistingUser(false);
    }
  }, [open, form]);

  const onSubmit = form.handleSubmit(async (v) => {
    setError(null);
    try {
      const res = await invite.mutateAsync({ email: v.email, role: jobRoleForLevel(v.level), message: v.message || undefined });
      if (res.existingUser) {
        toast.success(`${v.email} already has an account and was added to the job`);
        setExistingUser(true);
        onOpenChange(false);
        return;
      }
      toast.success(`Invitation sent to ${v.email}`);
      if (res.acceptUrl) setAcceptUrl(res.acceptUrl);
      else onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Invite to {job.name}</DialogTitle>
          <DialogDescription>If they already have an account in your organization they are added right away; otherwise they get a sign-up link by email.</DialogDescription>
        </DialogHeader>
        {acceptUrl && !existingUser ? (
          <div className="grid gap-4">
            <Alert>
              <MailIcon />
              <AlertTitle>Invitation created</AlertTitle>
              <AlertDescription>
                <p>Email delivery is simulated in this environment. Share this link directly:</p>
                <code className="bg-muted mt-1 block w-full break-all p-2 text-xs">{acceptUrl}</code>
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <CopyButton value={acceptUrl} label="Copy link" />
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="grid gap-5" noValidate>
            <FormError message={error} />
            <Field label="Email" htmlFor="m-email" required error={form.formState.errors.email?.message}>
              <Input id="m-email" type="email" autoFocus {...form.register('email')} />
            </Field>
            <Controller
              control={form.control}
              name="level"
              render={({ field }) => (
                <div className="grid gap-2">
                  <span className="text-sm font-medium">Access on this job</span>
                  <AccessLevelCards name="invite-level" options={pmLevelOptions(canGrant).filter((o) => canGrant || o.value !== 'pm_admin')} value={field.value} onChange={field.onChange} />
                </div>
              )}
            />
            <Field label="Personal note" htmlFor="m-msg" error={form.formState.errors.message?.message}>
              <Textarea id="m-msg" rows={2} placeholder="Optional" {...form.register('message')} />
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={invite.isPending}>
                {invite.isPending ? 'Sending…' : 'Send invitation'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddExistingUserDialog({ open, onOpenChange, existingIds }: { open: boolean; onOpenChange: (o: boolean) => void; existingIds: string[] }) {
  const { job, permissions } = useJobContext();
  const users = useUsers(open);
  const add = useAddMember(job.id);
  const [userId, setUserId] = useState<string>('');
  const [level, setLevel] = useState<PmLevel>('pm_user');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setUserId('');
      setLevel('pm_user');
      setError(null);
    }
  }, [open]);

  // Super Users already see every job; only Project Managers need a membership.
  const candidates = (users.data?.items ?? []).filter((u) => u.status === 'active' && u.tenantRole !== 'org_admin' && !existingIds.includes(u.id));

  const submit = async () => {
    if (!userId) return;
    setError(null);
    try {
      const res = await add.mutateAsync({ userId, role: jobRoleForLevel(level) });
      toast.success(`${res.member.fullName} added as ${LEVEL_LABEL[levelForJobRole(res.member.role)]}`);
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add an existing user</DialogTitle>
          <DialogDescription>Give a Project Manager who already belongs to your organization access to {job.name}.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-5">
          <FormError message={error} />
          <Field label="User" htmlFor="add-user">
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger id="add-user" className="w-full">
                <SelectValue placeholder={users.isPending ? 'Loading…' : candidates.length ? 'Choose a user' : 'Everyone is already on this job'} />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.fullName} · {u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid gap-2">
            <span className="text-sm font-medium">Access on this job</span>
            <AccessLevelCards name="add-level" options={pmLevelOptions(permissions.grantSend).filter((o) => permissions.grantSend || o.value !== 'pm_admin')} value={level} onChange={setLevel} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!userId || add.isPending}>
            {add.isPending ? 'Adding…' : 'Add to job'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

